import { createContext, useContext, useEffect, useState, useMemo, type ReactNode } from 'react';
import { getRealTimeConfigFromDb } from '../config/data/firebase';
import Loader from '../components/loader/Loader';
import {
	CYCLE_YEAR_OVERRIDE_ENABLED_KEY,
	CYCLE_YEAR_OVERRIDE_KEY,
	getEstCalendarYear,
	isCycleYearOverrideEnabled,
	parseCycleYearValue,
	resolveEffectiveCycleYear,
} from '../utils/cycleYear';

export type ConfigContextValue = Record<string, unknown>;

const ConfigContext = createContext<ConfigContextValue | undefined>(undefined);

const withEffectiveCycleYear = (raw: ConfigContextValue): ConfigContextValue => {
	const overrideEnabled = Boolean(raw[CYCLE_YEAR_OVERRIDE_ENABLED_KEY]);
	const overrideYear =
		parseCycleYearValue(raw[CYCLE_YEAR_OVERRIDE_KEY]) ??
		parseCycleYearValue(raw.CYCLE_YEAR) ??
		getEstCalendarYear();
	const {
		UPLOAD_KEY: _uploadKey,
		UNSUB_KEY: _unsubKey,
		PIN_KEY: _pinKey,
		RSVP_KEY: _rsvpKey,
		...safeRaw
	} = raw;
	return {
		...safeRaw,
		[CYCLE_YEAR_OVERRIDE_ENABLED_KEY]: overrideEnabled,
		[CYCLE_YEAR_OVERRIDE_KEY]: overrideYear,
		CYCLE_YEAR: resolveEffectiveCycleYear(raw),
	};
};

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
	const [rawConfig, setRawConfig] = useState<ConfigContextValue>({});
	const [estYear, setEstYear] = useState(() => getEstCalendarYear());

	useEffect(() => {
		const unsubscribe = getRealTimeConfigFromDb((data) => {
			try {
				setRawConfig((data as ConfigContextValue) || {});
			} catch (error) {
				console.error('Error setting config:', error);
			}
		});

		return () => {
			unsubscribe();
		};
	}, []);

	// Keep auto cycle year fresh across EST New Year without requiring a reload.
	useEffect(() => {
		if (!Object.keys(rawConfig).length || isCycleYearOverrideEnabled(rawConfig)) return;

		const syncEstYear = () => {
			const next = getEstCalendarYear();
			setEstYear((prev) => (prev === next ? prev : next));
		};

		syncEstYear();
		const intervalId = window.setInterval(syncEstYear, 60_000);
		const onVisible = () => {
			if (document.visibilityState === 'visible') syncEstYear();
		};
		document.addEventListener('visibilitychange', onVisible);

		return () => {
			window.clearInterval(intervalId);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, [rawConfig]);

	const value = useMemo<ConfigContextValue>(() => {
		const next = withEffectiveCycleYear(rawConfig);
		if (!isCycleYearOverrideEnabled(rawConfig)) {
			return { ...next, CYCLE_YEAR: estYear };
		}
		return next;
	}, [rawConfig, estYear]);

	if (!Object.keys(rawConfig).length) {
		return <Loader />;
	}

	return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

export const useConfig = (): ConfigContextValue => {
	const context = useContext(ConfigContext);
	if (context === undefined) {
		throw new Error('useConfig must be used within a ConfigProvider');
	}
	return context;
};
